/**
 * Toasts — M1-13. Provider + useToast() hook. Deterministic behavior:
 * auto-dismiss with pause-on-hover, capped stack, aria-live announcements
 * (danger toasts are assertive alerts).
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { CircleAlert, CircleCheck, Info, TriangleAlert, X } from "lucide-react";
import { cx } from "../utils/cx";

export type ToastIntent = "success" | "warning" | "danger" | "info";

export interface ToastOptions {
  readonly intent: ToastIntent;
  readonly title: ReactNode;
  readonly description?: ReactNode;
  /** ms; 0 = sticky until dismissed. Default 5000. */
  readonly duration?: number;
}

interface ToastRecord extends ToastOptions {
  readonly id: number;
  leaving: boolean;
}

interface ToastContextValue {
  readonly push: (toast: ToastOptions) => number;
  readonly dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
  return ctx;
}

const INTENT_ICON = {
  success: CircleCheck,
  warning: TriangleAlert,
  danger: CircleAlert,
  info: Info,
} as const;

const MAX_VISIBLE = 5;
const EXIT_MS = 140; // matches --duration-fast toast-out animation

export interface ToastProviderProps {
  readonly children: ReactNode;
  /** Close-button label for screen readers (localized by the app). */
  readonly closeLabel?: string;
}

export function ToastProvider({ children, closeLabel = "Dismiss" }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ReadonlyArray<ToastRecord>>([]);
  const nextId = useRef(1);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const remove = useCallback((id: number) => {
    const timer = timers.current.get(id);
    if (timer) clearTimeout(timer);
    timers.current.delete(id);
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const dismiss = useCallback(
    (id: number) => {
      // Play the exit animation, then drop.
      setToasts((current) => current.map((t) => (t.id === id ? { ...t, leaving: true } : t)));
      setTimeout(() => {
        remove(id);
      }, EXIT_MS);
    },
    [remove],
  );

  const schedule = useCallback(
    (id: number, duration: number) => {
      if (duration <= 0) return;
      timers.current.set(
        id,
        setTimeout(() => {
          dismiss(id);
        }, duration),
      );
    },
    [dismiss],
  );

  const push = useCallback(
    (options: ToastOptions) => {
      const id = nextId.current++;
      setToasts((current) => {
        const next = [...current, { ...options, id, leaving: false }];
        return next.length > MAX_VISIBLE ? next.slice(next.length - MAX_VISIBLE) : next;
      });
      schedule(id, options.duration ?? 5000);
      return id;
    },
    [schedule],
  );

  const pause = useCallback((id: number) => {
    const timer = timers.current.get(id);
    if (timer) clearTimeout(timer);
    timers.current.delete(id);
  }, []);

  const resume = useCallback(
    (toast: ToastRecord) => {
      if (!toast.leaving) schedule(toast.id, 2000);
    },
    [schedule],
  );

  const value = useMemo<ToastContextValue>(() => ({ push, dismiss }), [push, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {typeof document !== "undefined"
        ? createPortal(
            <div className="ui-toast-viewport ui-portal-root">
              {toasts.map((toast) => {
                const Icon = INTENT_ICON[toast.intent];
                return (
                  <div
                    key={toast.id}
                    className={cx("ui-toast", `ui-toast--${toast.intent}`, toast.leaving && "ui-toast--leaving")}
                    role={toast.intent === "danger" ? "alert" : "status"}
                    onMouseEnter={() => {
                      pause(toast.id);
                    }}
                    onMouseLeave={() => {
                      resume(toast);
                    }}
                  >
                    <span className="ui-toast-icon" aria-hidden="true">
                      <Icon size={18} />
                    </span>
                    <div className="ui-toast-content">
                      <p className="ui-toast-title">{toast.title}</p>
                      {toast.description ? <p className="ui-toast-description">{toast.description}</p> : null}
                    </div>
                    <button
                      type="button"
                      className="ui-toast-close"
                      aria-label={closeLabel}
                      onClick={() => {
                        dismiss(toast.id);
                      }}
                    >
                      <X size={14} aria-hidden="true" />
                    </button>
                  </div>
                );
              })}
            </div>,
            document.body,
          )
        : null}
    </ToastContext.Provider>
  );
}
